import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../queries/control-testing/keys.ts';
import { useAuditYear } from '../../contexts/AuditYearContext.tsx';
import toast from 'react-hot-toast';
import {
  Plus, Loader2, Download, RotateCcw, Play, Trash2, Pencil, ChevronRight, ChevronDown,
  ClipboardCheck, ClipboardList, BarChart2, ScrollText,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axios';
import { resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';
import { MODULE_KEYS } from '../../constants/moduleAccess.ts';
import { useRequireModuleAccess } from '../../hooks/useRequireModuleAccess.ts';
import ModuleAccessDeniedDialog from '../shared/ModuleAccessDeniedDialog.tsx';

import TestRunWizard from './TestRunWizard';
import { runAttributeTest } from '../attribute-result/runAttributeTest';
import { blobErrorMessage, downloadWorkingPaper } from '../../features/control-testing/report/download.ts';
import { buildAttributeList } from '../control-new-look/TestAttributesSubTab';
import EvidenceUploadModal from './EvidenceUploadModal';
import { groupTestRuns } from './groupRuns';
import { listTestRuns, mapRunForList, createTestRun, updateTestRun, getTestRun, runHasPopulation, runHasSamples, getRunSelectedSamples, isPopulationNotRequired, syncEvidenceSamples, getRunReadiness, describeReadiness } from '../../api/controlTestRuns';
import TestRunProgressModal from './TestRunProgressModal';
import RunLogModal from './RunLogModal';
import { invalidateRunLogQueries } from '../../hooks/control-testing/index.ts';
import AttributeTestResultModal from './AttributeTestResultModal';
import { FINISHED, periodLabel, StatusCell, ResultsCell, canDeleteAttempts } from './runDisplay';
import { deleteRuns } from '../../hooks/control-testing/useDeleteControlTestRun.ts';


function nowTime() {
  return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function emptyProgress(run) {
  return {
    runName: run?.test_run_name || (run?.test_run_number ? `RUN-${run.test_run_number}` : 'Test run'),
    messages: [],
    progress: 0,
    stage: 'starting',
    status: 'running',
    error: null,
  };
}

// activityLogEnabled: the run activity log (log icon, raw model call checkbox) is hidden unless the control view
// page was opened with ?activitylog=1 (see useActivityLogFeature).
export default function TestRunsTab({ control, controlUuid, activityLogEnabled = false }) {
  const navigate = useNavigate();
  const {
    checkAccess: checkTestExecutionsModuleAccess,
    dialogProps: testExecutionsModuleAccessDialogProps,
  } = useRequireModuleAccess(MODULE_KEYS.TEST_EXECUTIONS, {
    hint:
      'Creating, running, or re-running tests from a control requires the Test Executions module. Contact your organization administrator if you need access.',
  });
  const queryClient = useQueryClient();
  const { selectedAuditYearId, selectedAuditYear } = useAuditYear();
  const isYearLocked = Boolean(selectedAuditYear?.is_locked);
  const runsQueryKey = queryKeys.controlTesting.testRuns.byControl(controlUuid || '', selectedAuditYearId);

  const [busy, setBusy]       = useState(false);
  const [runningIds, setRunningIds] = useState(new Set()); // executions currently running
  const [showWizard, setShowWizard] = useState(false);
  const [editRun, setEditRun]       = useState(null); // reconfigure an existing run
  // The run a Rerun click created: deleted again if its wizard is closed without saving, so an
  // abandoned rerun never takes over the row from the finished run it would replace. A ref, not
  // state: the wizard calls onCreated and then onClose in the same tick.
  const pendingRerunIdRef = useRef(null);
  const [expandedRoots, setExpandedRoots] = useState(() => new Set()); // rows showing earlier attempts
  const toggleExpanded = (rootId) => setExpandedRoots((prev) => {
    const next = new Set(prev);
    next.has(rootId) ? next.delete(rootId) : next.add(rootId);
    return next;
  });
  const [freshControl, setFreshControl] = useState(null); // control with latest test_steps from test-plan API
  const [mapRun, setMapRun] = useState(null); // evidence upload modal (per-sample)
  const [resultsRunId, setResultsRunId] = useState(null); // unified attribute result modal
  const [logRun, setLogRun] = useState(null); // saved activity log modal ({ id, name })
  // Keep the text of every model call with the run's activity log (prompts, responses). This stores evidence
  // text, so the choice is never remembered: it starts unticked, and is reset whenever the feature is switched off.
  const [logRawCalls, setLogRawCalls] = useState(false);
  useEffect(() => {
    if (!activityLogEnabled) setLogRawCalls(false);
  }, [activityLogEnabled]);
  useEffect(() => {
    // Older builds remembered the tick in localStorage; forget it.
    try { localStorage.removeItem('run_log_raw_calls'); } catch { /* storage unavailable */ }
  }, []);
  const [progressByRunId, setProgressByRunId] = useState({});
  const [progressModalId, setProgressModalId] = useState(null);

  const controlRouteId = control?.control_id || controlUuid;

  // Auto-open results modal when a test run completes.
  // Waits 1.5 s so the user can see "Test complete — results saved." before
  // being taken straight to the Results page.
  useEffect(() => {
    if (!progressModalId) return;
    const prog = progressByRunId[progressModalId];
    if (prog?.status !== 'completed') return;
    const timer = setTimeout(() => {
      setProgressModalId(null);
      setResultsRunId(progressModalId);
    }, 1500);
    return () => clearTimeout(timer);
  }, [progressByRunId, progressModalId]);

  const appendProgress = useCallback((executionId, data, run) => {
    setProgressByRunId((prev) => {
      const cur = prev[executionId] || emptyProgress(run);
      const message = data.message || data.stage || 'Update';
      const stage = data.stage || cur.stage;

      // Start from the server-supplied value (or keep current).
      let nextProgress = typeof data.progress === 'number' && data.progress >= 0
        ? data.progress
        : cur.progress;

      // The AI service sends coarse values (e.g. stuck at 10 for the entire
      // analyzing phase, then a big jump at the end).  Derive a finer-grained
      // estimate so the bar advances smoothly as each attribute-sample pair
      // completes.
      //
      // Stage floors / ceilings:
      //   starting   →  5
      //   preparing  → 10–14
      //   analyzing  → 15–82  (interpolated from [X/Y] in the message)
      //   synthesizing → 88
      //   complete   → 100  (set by the caller, not here)
      if (stage === 'starting') {
        nextProgress = Math.max(nextProgress, 5);
      } else if (stage === 'preparing') {
        nextProgress = Math.max(nextProgress, 10);
      } else if (stage === 'analyzing') {
        // Parse "[X/Y]" that the AI includes in messages like "[3/4] → Starting…"
        // or "[4/4] × DONE: …".  Use the highest seen so far (never go back).
        const pairMatch = message.match(/\[(\d+)\/(\d+)\]/);
        if (pairMatch) {
          const done  = parseInt(pairMatch[1], 10);
          const total = parseInt(pairMatch[2], 10);
          if (total > 0) {
            // Map done/total → 15..82 so each completed pair advances the bar.
            const derived = Math.round(15 + (done / total) * 67);
            nextProgress = Math.max(nextProgress, derived);
          }
        } else {
          // No pair counter yet — just ensure we're past the preparing floor.
          nextProgress = Math.max(nextProgress, 15);
        }
      } else if (stage === 'synthesizing') {
        nextProgress = Math.max(nextProgress, 88);
      }

      // Never let the bar go backwards (guard against out-of-order events).
      nextProgress = Math.max(nextProgress, cur.progress);

      return {
        ...prev,
        [executionId]: {
          ...cur,
          runName: cur.runName || emptyProgress(run).runName,
          progress: nextProgress,
          stage,
          status: 'running',
          messages: [
            ...cur.messages,
            { time: nowTime(), stage: data.stage, message, outcome: data.outcome, kind: data.kind },
          ].slice(-120),
        },
      };
    });
  }, []);

  const openProgress = useCallback((executionId) => {
    if (executionId) setProgressModalId(executionId);
  }, []);

  // Clone sourceRun's full config (period + population + samples) into a brand-new
  // control_test_run record and return a minimal stub with the new executionId.
  // The caller decides what to do next (open wizard, execute, etc.).
  // supersedesRunId: the run the clone replaces (a rerun), which links them into one rerun chain.
  const cloneRun = useCallback(async (sourceRun, supersedesRunId = null) => {
    // Fetch the full run so population.items is available; fall back to list data.
    let fullRunData = null;
    try {
      const data = await getTestRun(sourceRun.executionId);
      fullRunData = data?.run || data;
    } catch { /* non-fatal */ }

    const pop = fullRunData?.population || sourceRun.population_data || {};
    // The listing row may not carry the testing period (only selected_periods): without it the rerun was saved and
    // executed with no period, so date checks could not be judged.
    // The run API returns timestamps ("2025-06-01T00:00:00Z"); the create endpoint reads plain dates only.
    const day = (v) => (v ? String(v).slice(0, 10) : null);
    const periodStart = day(sourceRun.testing_period_start || fullRunData?.testing_period_start
      || sourceRun.selected_periods?.periods?.[0]?.start);
    const periodEnd = day(sourceRun.testing_period_end || fullRunData?.testing_period_end
      || sourceRun.selected_periods?.periods?.slice(-1)?.[0]?.end);
    const sm  = sourceRun.sampling_method || 'statistical';
    const selectedSamples = getRunSelectedSamples(sourceRun);
    const notRequired = isPopulationNotRequired(sourceRun);

    // Strip any existing " (Rerun)" suffix so we don't stack them.
    const baseName = (sourceRun.test_run_name || 'Test run').replace(/ \(Rerun\)$/, '');

    // Step 1 — create the new run record with the same period/setup.
    const resp = await createTestRun({
      sox_control_id:       controlUuid,
      control_id:           control?.control_id,
      test_run_name:        `${baseName} (Rerun)`,
      test_type:            sourceRun.test_type            || 'toe',
      testing_method:       sourceRun.testing_method       || 'holistic',
      sampling_method:      sm,
      testing_period_start: periodStart,
      testing_period_end:   periodEnd,
      selected_periods:     sourceRun.selected_periods     || null,
      is_rollforward:       false,
      workflow_status:      'period_selected',
      population_status:    notRequired ? 'not_required' : 'provided',
      audit_year_id:        selectedAuditYearId || undefined,
      ...(supersedesRunId ? { supersedes_run_id: supersedesRunId, rerun_reason: 'Manual re-run' } : {}),
    });
    const newId = resp.id;

    // No population to carry over: keep the evidence-sample labels only. (Evidence
    // files are tagged per sample on the control, so the rerun picks them up by label.)
    if (notRequired) {
      await updateTestRun(newId, { workflow_status: 'population_configured', sampling_method: sm });
      if (selectedSamples.length > 0) await syncEvidenceSamples(newId, selectedSamples);
      return {
        executionId:          newId,
        test_run_number:      resp.run_number,
        test_run_name:        resp.test_run_name || `${baseName} (Rerun)`,
        test_type:            sourceRun.test_type            || 'toe',
        testing_method:       sourceRun.testing_method       || 'holistic',
        sampling_method:      sm,
        selected_periods:     sourceRun.selected_periods     || null,
        testing_period_start: periodStart,
        testing_period_end:   periodEnd,
        is_rollforward:       false,
        population_status:    'not_required',
        population_data:      { selected_samples: selectedSamples, sample_labels: Object.fromEntries(selectedSamples.map(x => [x, x])) },
        samples:              selectedSamples.map(ref => ({ record_ref: ref })),
        total_population:     null,
        total_sample_size:    selectedSamples.length,
        testStatus:           'population_configured',
        status:               'population_configured',
        workflow_status:      'population_configured',
      };
    }

    // Step 2 — carry over population + samples.
    await updateTestRun(newId, {
      workflow_status:  'population_configured',
      sampling_method:  sm,
      ipe_validated:    sourceRun.ipe_validated || false,
      population: {
        source_type:          pop.source_type           || 'file_upload',
        total_count:          pop.total_count           ?? sourceRun.total_population ?? null,
        identifier_column:    pop.identifier_column     || 'ID',
        // Keep each record's row (date, amount, vendor): it is what ties a sample to its own statement line. Without
        // it every sample was tested against every line of the statement.
        items: (pop.items || []).map(it => ({
          record_ref: typeof it === 'string' ? it : String(it?.record_ref ?? it),
          ...(it && typeof it === 'object' && it.row_data != null ? { row_data: it.row_data } : {}),
        })),
        population_file_id:   pop.population_file_id   || null,
        population_file_name: pop.population_file_name || pop.file_name || null,
        sample_labels:        pop.sample_labels        || null,
        ipe_proof_file_id:    pop.ipe_proof_file_id    || null,
        ipe_proof_file_name:  pop.ipe_proof_file_name  || null,
        // The tie-out figures of the files: without them the rerun's completeness check cannot be recomputed
        // (the files are not read again), and it reported the IPE proof as having no records.
        ...(pop.filtered_total_amount       != null ? { filtered_total_amount:       pop.filtered_total_amount       } : {}),
        ...(pop.independent_total_amount    != null ? { independent_total_amount:    pop.independent_total_amount    } : {}),
        ...(pop.ipe_population_record_count != null ? { ipe_population_record_count: pop.ipe_population_record_count } : {}),
        ...(pop.ipe_proof_record_count      != null ? { ipe_proof_record_count:      pop.ipe_proof_record_count      } : {}),
        ...(pop.ipe_population_column_count != null ? { ipe_population_column_count: pop.ipe_population_column_count } : {}),
        ...(pop.ipe_proof_column_count      != null ? { ipe_proof_column_count:      pop.ipe_proof_column_count      } : {}),
        ...(pop.ipe_source_note     ? { ipe_source_note:     pop.ipe_source_note     } : {}),
        ...(pop.ipe_comparison_note ? { ipe_comparison_note: pop.ipe_comparison_note } : {}),
        ...(pop.parse_metadata      ? { parse_metadata:      pop.parse_metadata      } : {}),
      },
      samples: selectedSamples.map(ref => ({ record_ref: ref, selection_method: sm })),
    });

    // Return a rich stub that mirrors the shape mapRunForList produces.
    // The wizard reads these fields at mount time to pre-fill BOTH steps:
    //   • selected_periods / test_type / testing_method / sampling_method → Step 1 (period & setup)
    //   • population_data / samples / ipe_validated → Step 2 (population pre-fill fallback
    //     while getTestRun() fetches the full record in the background)
    // Having selected_periods causes resumeStep() to land on Step 2 so the user
    // sees the pre-filled population immediately; the Back button takes them to the
    // pre-filled Step 1 if they want to change the period / test type.
    return {
      executionId:          newId,
      test_run_number:      resp.run_number,
      test_run_name:        resp.test_run_name || `${baseName} (Rerun)`,
      // Setup fields (Step 1 pre-fill)
      test_type:            sourceRun.test_type            || 'toe',
      testing_method:       sourceRun.testing_method       || 'holistic',
      sampling_method:      sm,
      selected_periods:     sourceRun.selected_periods     || null,
      testing_period_start: periodStart,
      testing_period_end:   periodEnd,
      is_rollforward:       false,
      // Population fields (Step 2 pre-fill fallback)
      population_data:      { ...pop, selected_samples: selectedSamples },
      samples:              selectedSamples.map(ref => ({ record_ref: ref, selection_method: sm })),
      ipe_validated:        sourceRun.ipe_validated || false,
      total_population:     pop.total_count ?? sourceRun.total_population ?? null,
      total_sample_size:    selectedSamples.length,
      // Status — 'population_configured' opens wizard directly at Step 2 (population).
      testStatus:           'population_configured',
      status:               'population_configured',
      workflow_status:      'population_configured',
    };
  }, [controlUuid, control]); // cloneRun

  // viewResults is kept for potential future use (e.g. from AttributeTestResultModal)
  // but the Eye icon is no longer rendered in the test runs table —
  // classic RunTestFlow is only reachable via the Overview page "Run Test" button.
  const viewResults = (executionId) =>
    navigate(`/sox-controls/${controlRouteId}/run-test`, { state: { viewExecutionId: executionId } });

  const runsQueryFn = useCallback(async () => {
    if (!controlUuid) return [];
    const rows = await listTestRuns(controlUuid, selectedAuditYearId);
    return rows.map((r) => mapRunForList(r));
  }, [controlUuid, selectedAuditYearId]);

  const { data: runs = [], isLoading: loading } = useQuery({
    queryKey: runsQueryKey,
    queryFn: runsQueryFn,
    enabled: !!controlUuid,
  });

  // fetchRuns — used by callers that need the refreshed list as a return value;
  // updates the cache and returns the new data.
  const fetchRuns = useCallback(async () => {
    return queryClient.fetchQuery({ queryKey: runsQueryKey, queryFn: runsQueryFn, staleTime: 0 });
  }, [queryClient, runsQueryKey, runsQueryFn]);

  // Fetch fresh control whenever wizard opens so attribute list reflects latest saved state
  const wizardOpen = showWizard || !!editRun;
  useEffect(() => {
    if (!wizardOpen || !controlUuid) return;
    axiosInstance.get(`/api/sox-controls/${controlUuid}`)
      .then(r => setFreshControl(r.data))
      .catch(() => setFreshControl(control));
  }, [wizardOpen, controlUuid]); // eslint-disable-line react-hooks/exhaustive-deps


  const boxes = groupTestRuns(runs);
  const controlTypeLabel = control?.control_type || control?.control_nature || '—';

  const isoDate = (v) =>
    typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)
      ? v
      : v ? String(v).split('T')[0] : null;

  // Execute the test in place using the ATTRIBUTE pipeline (new design): one
  // run per attribute -> its procedures -> per-sample results, persisted to
  // attribute_test_runs. The old step pipeline is no longer triggered here.
  // We still keep the test_executions row as the listing/launch record and
  // mark it completed so the row shows finished and the Sparkles (new result)
  // button appears.
  const executeRun = async (run, { openLog = true, analysisMode } = {}) => {
    if (!checkTestExecutionsModuleAccess()) return;
    const executionId = run.executionId;
    setRunningIds((prev) => new Set(prev).add(executionId));
    setProgressByRunId((prev) => ({
      ...prev,
      [executionId]: {
        ...emptyProgress(run),
        messages: [{ time: nowTime(), stage: 'starting', message: 'Connecting to test engine…' }],
      },
    }));
    if (openLog) setProgressModalId(executionId);

    try {
      const { result } = await runAttributeTest({
        controlUuid,
        controlId: control?.control_id,
        testingPeriodStart: isoDate(run.testing_period_start),
        testingPeriodEnd: isoDate(run.testing_period_end),
        existingRunId: executionId,
        // Never ask for raw capture while the feature is hidden, whatever an earlier session remembered.
        logRawCalls: activityLogEnabled && logRawCalls,
        analysisMode,
        onProgress: (data) => appendProgress(executionId, data, run),
      });

      setProgressByRunId((prev) => ({
        ...prev,
        [executionId]: {
          ...(prev[executionId] || emptyProgress(run)),
          status: 'completed',
          progress: 100,
          stage: 'complete',
          messages: [
            ...(prev[executionId]?.messages || []),
            { time: nowTime(), stage: 'complete', message: 'Test complete — results saved.' },
          ],
        },
      }));

      // Reflect completion on the listing row (attribute results already
      // persisted server-side to attribute_test_runs). Non-fatal if it fails.
      try {
        await updateTestRun(executionId, { workflow_status: 'completed' });
      } catch { /* execution record is source of truth */ }
      return result;
    } catch (err) {
      const errMsg = resolve403ErrorMessage(err, err.response?.data?.error || err.message || 'Test run failed');
      setProgressByRunId((prev) => ({
        ...prev,
        [executionId]: {
          ...(prev[executionId] || emptyProgress(run)),
          status: 'failed',
          stage: 'error',
          error: errMsg,
          messages: [
            ...(prev[executionId]?.messages || []),
            { time: nowTime(), stage: 'error', message: errMsg },
          ],
        },
      }));
      setProgressModalId(executionId);
    } finally {
      setRunningIds((prev) => { const n = new Set(prev); n.delete(executionId); return n; });
      // The run keeps its id when it is executed again, and a new activity log replaces the old one.
      void invalidateRunLogQueries(queryClient, executionId);
      await fetchRuns();
    }
  };

  // Run: enforce the full test-plan -> configure -> execute chain before the
  // attribute pipeline initiates. The pipeline can only produce the new-design
  // results when every prerequisite is in place.
  //
  //   1. Test plan : attributes + their procedures exist on the control.
  //   2. Attribute table : the fields to validate in evidence are configured.
  //   3. Configure : population + sample selection on this run.
  //   4. Evidence : files uploaded AND mapped.
  //
  // Missing population routes the user to the reconfigure modal; missing
  // evidence routes to the evidence modal. Everything else is an actionable
  // message.
  const handleRun = async (box) => {
    const run = box.current;

    // 1. Test plan — attributes + procedures
    const attrs = buildAttributeList(control?.test_steps || []);
    const topAttrs = Array.isArray(control?.test_attributes) ? control.test_attributes : [];
    const hasAttributes = attrs.length > 0 || topAttrs.length > 0;
    const hasProcedures = topAttrs.length > 0
      ? topAttrs.some(a => (a.procedure?.length || a.steps?.length))
      : attrs.some(a => (a.procedure && String(a.procedure).trim()));
    if (!hasAttributes || !hasProcedures) {
      toast.error('Test plan incomplete: add test attributes and their testing procedures/steps for this control before running.');
      return;
    }

    // 2. Attribute table — fields to validate. The table auto-derives its
    // columns from the attributes, so attributes-present (checked in #1) is
    // sufficient; an explicitly-saved attribute_table_config is optional.

    // 3. Configure — population + sample selection on this run
    if (isPopulationNotRequired(run) && !runHasSamples(run)) {
      // No population for this run — samples are groups of evidence files.
      toast.error('Add evidence files and group them into samples before running (use the evidence action).');
      setMapRun(run);
      return;
    }
    if (!runHasPopulation(run) || !runHasSamples(run)) {
      toast.error('Configure this run first: select the population and pick the sample from it (use the reconfigure action).');
      setEditRun(run);
      return;
    }

    // 4. Ask the server (the same rules it enforces on execute) and, for evidence, which
    //    samples have nothing mapped to them. Problems block; missing evidence only warns.
    let checkedByServer = false;
    try {
      const { blocking, warning, inputWarnings, issueCodes } = describeReadiness(await getRunReadiness(run.executionId));
      checkedByServer = true;
      if (blocking) {
        toast.error(blocking, { duration: 8000 });
        if (issueCodes.includes('no_samples') && isPopulationNotRequired(run)) setMapRun(run);
        else if (issueCodes.includes('no_population')) setEditRun(run);
        return;
      }
      if (warning) toast(warning, { icon: '⚠️', duration: 8000 });
      inputWarnings.forEach((w) => toast(w.message, { icon: w.severity === 'error' ? '⛔' : '⚠️', duration: 12000 }));
    } catch {
      // The server enforces readiness again on execute; fall back to the simple check below.
    }
    if (!checkedByServer) {
      try {
        const evidenceResp = await axiosInstance.get(`/api/sox-controls/${controlUuid}/evidence`);
        const hasAnyEvidence = Array.isArray(evidenceResp.data) && evidenceResp.data.length > 0;
        if (!hasAnyEvidence) {
          toast('No evidence files found for this control. The AI will run without evidence — results may be incomplete.', { icon: '⚠️', duration: 5000 });
        }
      } catch {
        // Non-fatal — proceed if we cannot verify.
      }
    }

    // All prerequisites satisfied — initiate the attribute pipeline.
    await executeRun(run);
  };

  const handleRerun = async (box) => {
    if (!checkTestExecutionsModuleAccess()) return;
    setBusy(true);
    try {
      const run = box.current;
      const isNewStyle = typeof run.executionId === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(run.executionId);
      if (isNewStyle) {
        // Clone into a new run record then immediately open the wizard pre-filled:
        //   • Wizard opens at Step 2 (population pre-filled from cloned data)
        //   • Back button takes user to Step 1 (period/setup also pre-filled)
        //   • User clicks through without changes OR edits anything they like
        //   • After wizard saves → Evidence Upload modal opens on the new run
        //   • After evidence is confirmed → test executes
        // Link the rerun to the row's newest attempt: a chain has one newest run.
        const newest = box.attempts?.[0] || run;
        const newRunStub = await cloneRun(run, newest.executionId);
        pendingRerunIdRef.current = newRunStub.executionId;
        setFreshControl(null);
        setEditRun(newRunStub);
      } else {
        // Legacy test_executions system: create a superseding execution first
        const resp = await axiosInstance.post(`/api/test-executions/${run.executionId}/rerun`, {
          rerun_reason: 'Manual re-run',
        });
        const newId = resp.data.new_execution_id;
        const updated = await fetchRuns();
        const newRun = (updated || []).find((r) => r.executionId === newId)
          || { executionId: newId, testing_period_start: run.testing_period_start, testing_period_end: run.testing_period_end };
        if (newId) await executeRun(newRun);
      }
    } catch (err) {
      toast.error('Rerun failed: ' + resolve403ErrorMessage(err, err.response?.data?.error || err.message));
    } finally {
      setBusy(false);
    }
  };

  const [downloadMenuId, setDownloadMenuId] = useState(null);

  // The same working paper as the results page: built from what the result tabs show, then laid out.
  const handleDownload = async (runId, format) => {
    setDownloadMenuId(null);
    setBusy(true);
    try {
      await downloadWorkingPaper(runId, format, {}, control ? { control } : {});
    } catch (err) {
      toast.error('Download failed: ' + resolve403ErrorMessage(err, await blobErrorMessage(err)));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (box) => {
    if (!window.confirm(`Delete this entire test run (${box.attempts.length} attempt${box.attempts.length > 1 ? 's' : ''})? This cannot be undone.`)) return;
    setBusy(true);
    try {
      // Attempts already gone count as deleted, so a retry after a partial failure only targets the rest.
      const { remaining, firstError } = await deleteRuns(box.attempts.map((a) => a.executionId));
      if (remaining.length > 0) {
        const msg = resolve403ErrorMessage(firstError, firstError?.response?.data?.error || firstError?.message);
        toast.error(`Delete failed for ${remaining.length} of ${box.attempts.length} attempt(s): ${msg}`);
      }
    } finally {
      await fetchRuns();
      setBusy(false);
    }
  };

  // Wizard closed: a rerun it was opened for that was never saved is removed again (the backend then
  // makes the run it would have replaced current again).
  const closeWizard = async () => {
    const pending = pendingRerunIdRef.current;
    pendingRerunIdRef.current = null;
    setShowWizard(false); setEditRun(null); setFreshControl(null);
    if (pending) {
      const { remaining } = await deleteRuns([pending]);
      if (remaining.length > 0) toast.error('The unsaved rerun could not be removed; delete it from its row.');
    }
    fetchRuns();
  };

  const iconBtn = 'p-1.5 rounded-md disabled:opacity-50';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-0.5">Test Runs</h2>
          <p className="text-sm text-gray-500">{boxes.length} test run{boxes.length !== 1 ? 's' : ''} for this control</p>
        </div>
        <div className="flex items-center gap-2">
          {activityLogEnabled && (
            <label
              className="inline-flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none"
              title="Keep the prompts, tool output and responses of every model call with the run's activity log (kept 14 days). They contain evidence text."
            >
              <input type="checkbox" checked={logRawCalls} onChange={(e) => setLogRawCalls(e.target.checked)} />
              Record raw model calls
            </label>
          )}
          <button
            onClick={() => {
              if (isYearLocked) return;
              navigate(`/sox-controls/${controlUuid}/run-test`, {
                state: { runTestReturnPath: `/sox-controls/view/${controlUuid}` },
              });
            }}
            disabled={isYearLocked}
            title={isYearLocked ? 'This audit year is locked — no changes permitted' : undefined}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ClipboardList className="h-4 w-4" /> Classic Test
          </button>
          <button
            onClick={() => {
              if (isYearLocked) return;
              if (!checkTestExecutionsModuleAccess()) return;
              setFreshControl(null);
              setShowWizard(true);
            }}
            disabled={isYearLocked}
            title={isYearLocked ? 'This audit year is locked — no changes permitted' : undefined}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="h-4 w-4" /> New Test Run
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading test runs…
        </div>
      )}

      {!loading && boxes.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <p className="text-sm font-medium text-gray-700 mb-1">No test runs yet</p>
          <p className="text-xs text-gray-500">Click "Create New Test Run" to get started.</p>
        </div>
      )}

      {!loading && boxes.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-600 uppercase tracking-wide">
                <th className="px-4 py-3 text-left">Test Name</th>
                <th className="px-4 py-3 text-left">Run ID</th>
                <th className="px-4 py-3 text-left">Period</th>
                <th className="px-4 py-3 text-left">Population</th>
                <th className="px-4 py-3 text-left">Sample</th>
                <th className="px-4 py-3 text-left">Control Type</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Results</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {boxes.map(box => {
                const r = box.current;
                const running = runningIds.has(r.executionId);
                const boxStatus = (box.status || '').toLowerCase();
                const finished       = FINISHED.includes(boxStatus);
                const isSignedOff    = boxStatus === 'signed_off';
                const isFailed       = boxStatus === 'failed';
                const isInReview     = boxStatus === 'prepared' || boxStatus === 'in_review';
                const isPreExecution = !finished;

                // State-based visibility — Eye is removed from this table entirely
                // (classic RunTestFlow is only reachable via the Overview "Run Test" button).
                //   Pre-execution  → Pencil + ClipboardCheck + Run
                //   Completed/Failed → BarChart2 + Download + Rerun (opens wizard)
                //   Signed Off       → BarChart2 (read-only) + Download
                const canReconfigure    = isPreExecution || isFailed;
                const canUploadEvidence = isPreExecution;
                // Completed or failed only: a run in review or signed off is not rerun (the backend refuses too).
                const canRerun          = finished && !isSignedOff && !isInReview;
                const canDelete         = canDeleteAttempts(box.attempts.map((a) => a.status || a.workflow_status));
                const expanded          = box.attempts.length > 1 && expandedRoots.has(box.rootId);

                const disabled = busy || running;
                return (
                  <React.Fragment key={box.rootId}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">{r.test_run_name || 'Untitled'}</td>
                    <td className="px-4 py-3 text-gray-500">RUN-{r.test_run_number ?? '?'}
                      {box.attempts.length > 1 && (
                        <button type="button" onClick={() => toggleExpanded(box.rootId)}
                          aria-expanded={expanded}
                          title={expanded ? 'Hide earlier attempts' : 'Show earlier attempts'}
                          className="inline-flex items-center text-gray-400 hover:text-gray-700 ml-1">
                          · {box.attempts.length}×
                          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{periodLabel(r)}</td>
                    <td className="px-4 py-3 text-gray-600">{isPopulationNotRequired(r) ? <span className="text-xs italic text-gray-500">Not required</span> : (r.total_population ?? '—')}</td>
                    <td className="px-4 py-3 text-gray-600">{r.total_sample_size ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-600 capitalize">{controlTypeLabel}</td>
                    <td className="px-4 py-3">
                      <StatusCell
                        status={box.status}
                        workflowStatus={r.workflow_status}
                        running={running}
                        onRunningClick={() => openProgress(r.executionId)}
                        populationNotRequired={isPopulationNotRequired(r)}
                      />
                    </td>
                    <td className="px-4 py-3"><ResultsCell run={r} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-0.5">
                        {/* ── Pre-execution actions ── */}
                        {canReconfigure && (
                          <button onClick={() => {
                            if (!checkTestExecutionsModuleAccess()) return;
                            setEditRun(r);
                          }} disabled={disabled} title="Reconfigure (period / population / name)"
                            className={`${iconBtn} text-gray-500 hover:bg-gray-100`}><Pencil className="h-4 w-4" /></button>
                        )}
                        {canUploadEvidence && (
                          <button onClick={() => setMapRun(r)} disabled={disabled}
                            title="Upload & map evidence"
                            className={`${iconBtn} text-indigo-600 hover:bg-indigo-50`}
                          ><ClipboardCheck className="h-4 w-4" /></button>
                        )}

                        {/* ── Execution-state actions ── */}
                        {running ? (
                          <button
                            type="button"
                            onClick={() => openProgress(r.executionId)}
                            className={`${iconBtn} text-indigo-600 hover:bg-indigo-50`}
                            title="View test progress"
                          >
                            <Loader2 className="h-4 w-4 animate-spin" />
                          </button>
                        ) : finished ? (
                          /* Completed / Failed / Signed Off — results view + download (+ rerun if not signed off) */
                          <>
                            <button onClick={() => setResultsRunId(r.executionId)} disabled={busy}
                              title={isSignedOff ? 'View results (signed off)' : 'Results, samples & sign-off'}
                              className={`${iconBtn} text-indigo-600 hover:bg-indigo-50`}><BarChart2 className="h-4 w-4" /></button>
                            {activityLogEnabled && (
                              <button onClick={() => setLogRun({ id: r.executionId, name: r.test_run_name || 'Untitled' })}
                                title="Activity log" aria-label="Activity log"
                                className={`${iconBtn} text-gray-500 hover:bg-gray-100`}><ScrollText className="h-4 w-4" /></button>
                            )}
                            <div className="relative">
                              <button onClick={() => setDownloadMenuId(downloadMenuId === r.executionId ? null : r.executionId)}
                                disabled={busy} title="Download report"
                                className={`${iconBtn} text-gray-500 hover:bg-gray-100`}><Download className="h-4 w-4" /></button>
                              {downloadMenuId === r.executionId && (
                                <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded shadow-lg z-50">
                                  <button className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                                    onClick={() => handleDownload(r.executionId, 'pdf')}>PDF Working Paper</button>
                                  <button className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                                    onClick={() => handleDownload(r.executionId, 'excel')}>Excel Working Paper</button>
                                </div>
                              )}
                            </div>
                            {canRerun && (
                              <button onClick={() => handleRerun(box)} disabled={busy}
                                title="Rerun — copies this run and opens it for editing before executing"
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md disabled:opacity-50 transition-colors">
                                <RotateCcw className="h-3.5 w-3.5" /> Rerun
                              </button>
                            )}
                          </>
                        ) : (
                          /* Not yet executed — Run button */
                          <button onClick={() => handleRun(box)} disabled={busy} title="Run test"
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-md disabled:opacity-50 transition-colors">
                            <Play className="h-3.5 w-3.5" /> Run
                          </button>
                        )}
                        {canDelete && (
                          <button onClick={() => handleDelete(box)} disabled={disabled} title="Delete"
                            className={`${iconBtn} text-red-500 hover:bg-red-50`}><Trash2 className="h-4 w-4" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expanded && box.attempts.slice(1).map((a) => {
                    const aFinished = FINISHED.includes(String(a.status || '').toLowerCase());
                    return (
                      <tr key={a.executionId} className="bg-gray-50/60 text-xs">
                        <td className="pl-8 pr-4 py-2 text-gray-500">↳ Earlier attempt</td>
                        <td className="px-4 py-2 text-gray-500">RUN-{a.test_run_number ?? '?'}</td>
                        <td className="px-4 py-2 text-gray-500">{periodLabel(a)}</td>
                        <td className="px-4 py-2 text-gray-500">{isPopulationNotRequired(a) ? 'Not required' : (a.total_population ?? '—')}</td>
                        <td className="px-4 py-2 text-gray-500">{a.total_sample_size ?? '—'}</td>
                        <td className="px-4 py-2 text-gray-500 capitalize">{controlTypeLabel}</td>
                        <td className="px-4 py-2">
                          <StatusCell status={a.status} workflowStatus={a.workflow_status} running={false}
                            populationNotRequired={isPopulationNotRequired(a)} />
                        </td>
                        <td className="px-4 py-2"><ResultsCell run={a} /></td>
                        <td className="px-4 py-2">
                          {aFinished && (
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => setResultsRunId(a.executionId)} disabled={busy}
                                title="Results of this attempt" aria-label="Results of this attempt"
                                className={`${iconBtn} text-indigo-600 hover:bg-indigo-50`}><BarChart2 className="h-4 w-4" /></button>
                              <button onClick={() => handleDownload(a.executionId, 'pdf')} disabled={busy}
                                className="px-1.5 py-0.5 text-gray-600 hover:bg-gray-100 rounded">PDF</button>
                              <button onClick={() => handleDownload(a.executionId, 'excel')} disabled={busy}
                                className="px-1.5 py-0.5 text-gray-600 hover:bg-gray-100 rounded">Excel</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Evidence upload — per sample, then run */}
      {mapRun && (
        <EvidenceUploadModal
          control={control}
          controlUuid={controlUuid}
          run={mapRun}
          onClose={() => { setMapRun(null); void fetchRuns(); }}
          onSaved={async ({ analysisMode } = {}) => {
            // Evidence upload is only reachable for pre-execution runs (ClipboardCheck
            // is hidden once a run is completed or signed off). Always execute in place.
            const run = mapRun;
            setMapRun(null);
            await executeRun(run, { analysisMode });
          }}
        />
      )}

      {/* Unified attribute results modal (Results + Samples + Sign-Off) */}
      {activityLogEnabled && logRun && (
        <RunLogModal
          runId={logRun.id}
          runName={logRun.name}
          onClose={() => setLogRun(null)}
          onOpenResults={() => { setResultsRunId(logRun.id); setLogRun(null); }}
        />
      )}
      {resultsRunId && (
        <AttributeTestResultModal
          runId={resultsRunId}
          onClose={() => { setResultsRunId(null); fetchRuns(); }}
        />
      )}

      {progressModalId && (
        <TestRunProgressModal
          progress={progressByRunId[progressModalId]}
          onClose={() => setProgressModalId(null)}
        />
      )}

      {/* Create / reconfigure wizard */}
      {(showWizard || editRun) && (
        <TestRunWizard
          control={freshControl || control}
          controlUuid={controlUuid}
          resumeRun={editRun}
          onClose={() => { void closeWizard(); }}
          onCreated={async ({ runId } = {}) => {
            pendingRerunIdRef.current = null; // saved: the rerun stays
            setShowWizard(false);
            setEditRun(null);
            setFreshControl(null);
            const updated = await fetchRuns();
            if (runId) {
              const run = (updated || []).find(r => r.executionId === runId || r.id === runId);
              if (run) setMapRun(run);
            }
          }}
        />
      )}

      <ModuleAccessDeniedDialog {...testExecutionsModuleAccessDialogProps} />
    </div>
  );
}
